import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  apiErrorText,
  type CeoBankConnection,
  type CeoBankConnectResult,
  useDeleteCeoBankConnectionMutation,
  useFinishCeoBankOauthMutation,
  useListCeoBankConnectionsQuery,
  useSyncCeoBankConnectionMutation,
} from '@shared/api';
import { Card } from '@shared/ui/Card';
import { BANK_PROVIDER_NAMES, formatDateTimeRu } from '../ceoFormat';
import { WrapBadge, WrapButton } from '../ui';
import { ConnectBankForm } from './ConnectBankForm';
import { SandboxLoginForm } from './SandboxLoginForm';
import {
  ConnectionActions,
  ConnectionError,
  ErrorNotice,
  FormHint,
  HeaderRow,
  Section,
  SubTitle,
  SuccessNotice,
  Table,
  TableWrap,
} from './styles';

/** Сколько дней до конца срока ключа предупреждать. */
const EXPIRY_WARNING_DAYS = 14;
const DAY_MS = 24 * 60 * 60 * 1000;
/** Обмен идёт в фоне: состояние подключений перечитывается, пока экран открыт. */
const POLL_MS = 15_000;

function connectionStatus(c: CeoBankConnection) {
  if (c.syncing) return { tone: 'info', text: 'Идёт обмен' } as const;
  if (c.status === 'pending') return { tone: 'warning', text: 'Нужен вход в банк' } as const;
  if (c.status === 'error') return { tone: 'danger', text: 'Ошибка обмена' } as const;
  if (c.sandbox) {
    return c.last_error
      ? ({ tone: 'danger', text: 'Проверка не пройдена' } as const)
      : ({ tone: 'info', text: 'Проверка пройдена' } as const);
  }
  return { tone: 'success', text: 'Работает' } as const;
}

function expiryWarning(c: CeoBankConnection): string | null {
  if (!c.expires_at) return null;
  const days = Math.ceil((new Date(c.expires_at).getTime() - Date.now()) / DAY_MS);
  if (days > EXPIRY_WARNING_DAYS) return null;
  return days > 0 ? `Ключ истекает через ${days} дн. Замените его заранее.` : 'Срок ключа истёк.';
}

interface BankConnectionsSectionProps {
  canWrite: boolean;
  /** Обмен закончился: счета и операции надо перечитать. */
  onSynced: () => void;
}

export function BankConnectionsSection({ canWrite, onSynced }: BankConnectionsSectionProps) {
  const { data } = useListCeoBankConnectionsQuery(undefined, { pollingInterval: POLL_MS });
  const [syncConnection] = useSyncCeoBankConnectionMutation();
  const [deleteConnection] = useDeleteCeoBankConnectionMutation();
  const [finishOauth] = useFinishCeoBankOauthMutation();
  const [params, setParams] = useSearchParams();
  const [form, setForm] = useState<'new' | CeoBankConnection | null>(null);
  const [sandboxLogin, setSandboxLogin] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const connections = data?.connections ?? [];
  const canManage = canWrite && Boolean(data?.can_manage);

  // Обмен идёт в фоне. Новая отметка времени значит, что он закончился.
  const syncStamp = connections.map((c) => c.last_sync_at).join('|');
  const lastStamp = useRef<string | null>(null);
  useEffect(() => {
    if (!data) return;
    if (lastStamp.current !== null && lastStamp.current !== syncStamp) onSynced();
    lastStamp.current = syncStamp;
  }, [data, syncStamp, onSynced]);

  // Возврат со страницы входа в банк: банк приводит сюда с одноразовым кодом.
  const oauthHandled = useRef(false);
  useEffect(() => {
    if (!canWrite || oauthHandled.current) return;
    const code = params.get('code');
    const state = params.get('state');
    const bankError = params.get('error');
    if (!bankError && !(code && state)) return;
    oauthHandled.current = true;
    setParams({}, { replace: true });
    if (bankError || !code || !state) {
      const reason = bankError ? ` (${bankError})` : '';
      setError(`Банк не выдал доступ${reason}. Нажмите «Войти в банк» и повторите вход.`);
      return;
    }
    finishOauth({ code, state })
      .unwrap()
      .then((res) =>
        setNotice(
          res.check ?? `Банк подключён, найдено счетов: ${res.accounts_found}. Забираем выписки.`,
        ),
      )
      .catch((err: unknown) => setError(apiErrorText(err, 'Не удалось завершить вход в банк')));
  }, [canWrite, params, setParams, finishOauth]);

  function handleConnected(result: CeoBankConnectResult) {
    if (result.authorize_url && result.connection.sandbox) {
      setForm(null);
      setError(null);
      setSandboxLogin(result.authorize_url);
      return;
    }
    if (result.authorize_url) {
      window.location.assign(result.authorize_url);
      return;
    }
    setForm(null);
    setError(null);
    setNotice(
      result.check ??
        `Банк подключён, найдено счетов: ${result.accounts_found}. Забираем выписки.`,
    );
  }

  async function handleSync(c: CeoBankConnection) {
    try {
      setError(null);
      await syncConnection(c.id).unwrap();
      setNotice(`${c.label}: обмен запущен, данные появятся через несколько минут.`);
    } catch (err: unknown) {
      setError(apiErrorText(err, 'Не удалось запустить обмен'));
    }
  }

  async function handleDelete(c: CeoBankConnection) {
    const question =
      `Отключить «${c.label}»? Ключ будет удалён. Счета и их история останутся, ` +
      'дальше они ведутся вручную.';
    if (!window.confirm(question)) return;
    try {
      setError(null);
      const res = await deleteConnection(c.id).unwrap();
      setNotice(`Подключение удалено. Счетов переведено на ручной учёт: ${res.unlinked_accounts}.`);
      onSynced();
    } catch (err: unknown) {
      setError(apiErrorText(err, 'Не удалось удалить подключение'));
    }
  }

  return (
    <Section>
      <HeaderRow>
        <div>
          <SubTitle>Подключения к банкам</SubTitle>
          <FormHint>
            Подключённый банк сам присылает остатки и операции. Счета без подключения ведутся
            вручную: выпиской файлом или вводом остатка. По ВТБ, ПСБ, Газпромбанку и ВБРР прямого
            обмена пока нет.
          </FormHint>
        </div>
        {canManage && data?.vault_ready && (
          <WrapButton $size="sm" $variant="secondary" onClick={() => setForm('new')}>
            + Подключить банк
          </WrapButton>
        )}
      </HeaderRow>

      {data && !data.vault_ready && (
        <Card>
          <ErrorNotice>
            На сервере нет ключа шифрования, поэтому подключения недоступны. Счета можно вести
            вручную.
          </ErrorNotice>
        </Card>
      )}
      {notice && (
        <Card>
          <SuccessNotice>{notice}</SuccessNotice>
        </Card>
      )}
      {error && (
        <Card>
          <ErrorNotice>{error}</ErrorNotice>
        </Card>
      )}
      {form && (
        <ConnectBankForm
          key={form === 'new' ? 'new' : form.id}
          connection={form === 'new' ? undefined : form}
          onDone={handleConnected}
          onClose={() => setForm(null)}
        />
      )}
      {sandboxLogin && (
        <SandboxLoginForm
          key={sandboxLogin}
          authorizeUrl={sandboxLogin}
          onDone={(result) => {
            setSandboxLogin(null);
            setNotice(result.check);
          }}
          onClose={() => setSandboxLogin(null)}
        />
      )}

      {connections.length > 0 && (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>Банк и компания</th>
                <th>Счетов</th>
                <th>Состояние</th>
                <th>Последний обмен</th>
                {canManage && <th />}
              </tr>
            </thead>
            <tbody>
              {connections.map((c) => {
                const status = connectionStatus(c);
                const expiry = expiryWarning(c);
                return (
                  <tr key={c.id}>
                    <td>
                      <strong>
                        {BANK_PROVIDER_NAMES[c.provider]}
                        {c.sandbox && ', песочница'}
                      </strong>
                      <div>{c.label}</div>
                    </td>
                    <td>{c.accounts_count}</td>
                    <td>
                      <WrapBadge $tone={status.tone}>{status.text}</WrapBadge>
                      {c.last_error && <ConnectionError>{c.last_error}</ConnectionError>}
                      {expiry && <ConnectionError>{expiry}</ConnectionError>}
                    </td>
                    <td>{formatDateTimeRu(c.last_success_at)}</td>
                    {canManage && (
                      <td>
                        <ConnectionActions>
                          {c.status !== 'pending' && !c.sandbox && (
                            <WrapButton
                              $size="sm"
                              $variant="ghost"
                              disabled={c.syncing}
                              onClick={() => handleSync(c)}
                            >
                              Обновить
                            </WrapButton>
                          )}
                          <WrapButton $size="sm" $variant="ghost" onClick={() => setForm(c)}>
                            {c.status === 'pending' || (c.sandbox && c.provider === 'alfa_api')
                              ? 'Войти в банк'
                              : 'Заменить ключ'}
                          </WrapButton>
                          <WrapButton $size="sm" $variant="ghost" onClick={() => handleDelete(c)}>
                            Отключить
                          </WrapButton>
                        </ConnectionActions>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </TableWrap>
      )}
    </Section>
  );
}
